import React, { useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';

import FeaturedArticle from '@components/FeedItems/FeaturedArticle';
import Spinner from '@components/Spinner';
import useEventTracking from '@hooks/useEventTracking';
import type { Article } from '@interfaces/homepage';
import { articles_feed_path } from '@routes';
import { ClickTarget, TrackingLocation, click, impression } from '@tracking';
import { getUserRecommendedArticles } from '@utils/backend-api/articles';
import type { User } from '@utils/backend-api/currentUser';

import ForYouSection from './Section';
import { FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS, VIEW_LATEST_ARTICLES } from './constants';
import useRegisterSection from './useRegisterSection';

const ForYouRecommendedArticles = ({ initialCurrentUser }: { initialCurrentUser?: User }) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.RecommendedArticles,
  });
  const [articles, setArticles] = useState<Article[]>([]);
  const [popular, setPopular] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const recommendedArticlesDisplayText = useMemo(() => {
    if (!initialCurrentUser) return FOR_YOU_ENGAGEMENT_LABELS.Engaged.NonPro.RecommendedArticles;
    const isPro = initialCurrentUser?.is_pro_subscriber;

    if (popular) {
      return FOR_YOU_ENGAGEMENT_LABELS.NotEngaged.NonPro.RecommendedArticles;
    }

    return isPro
      ? FOR_YOU_ENGAGEMENT_LABELS.Engaged.Pro.RecommendedArticles
      : FOR_YOU_ENGAGEMENT_LABELS.Engaged.NonPro.RecommendedArticles;
  }, [initialCurrentUser, popular]);

  useEffect(() => {
    let cancelled = false;

    const fetchArticles = async () => {
      try {
        const { articles, popular } = await getUserRecommendedArticles();
        if (!cancelled) {
          setArticles(articles);
          setPopular(popular);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchArticles();

    return () => {
      cancelled = true;
    };
  }, []);

  useRegisterSection(FOR_YOU_SECTION_IDS.RecommendedArticles, isLoading || articles?.length > 0);

  if (!isLoading && articles?.length === 0) return null;

  const handleViewAllClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.AnchorLink, clickText: VIEW_LATEST_ARTICLES }));
  };

  return (
    <ForYouSection
      id={FOR_YOU_SECTION_IDS.RecommendedArticles}
      highlight={recommendedArticlesDisplayText.label}
      subtitle={recommendedArticlesDisplayText.subtitle}
      title={recommendedArticlesDisplayText.title}
    >
      <Track>
        <InView
          onChange={(inView) => {
            inView &&
              trackEvent(
                impression({ impressionElement: FOR_YOU_SECTION_IDS.RecommendedArticles })
              );
          }}
          triggerOnce
        >
          {isLoading ? (
            <Spinner marginY="small" />
          ) : (
            <div className="mt-32 flex flex-col gap-32">
              <div className="-mx-16 flex flex-row flex-nowrap gap-32 overflow-x-auto px-16 sm:mx-0 sm:flex-wrap sm:gap-y-72 sm:overflow-x-hidden sm:px-0 nav:flex-nowrap">
                {articles.map((article) => (
                  <FeaturedArticle
                    key={article.id}
                    className="min-w-[65svw] !border-0 !px-0 !pb-0 sm:min-w-[calc(50svw-72px)] nav:min-w-0 [&_[data-name='article_highlight']]:text-overline-sm sm:[&_[data-name='article_highlight']]:text-overline-md [&_a:first-of-type]:!mx-0 [&_div>div>div]:hidden sm:[&_div>div>div]:inline [&_h2]:font-suisse-intl [&_h2]:text-body-md [&_h2]:font-normal sm:[&_h2]:font-suisse-works sm:[&_h2]:text-headline-xxs [&_p]:hidden"
                    featuredArticle={article}
                    isFluid
                  />
                ))}
              </div>
              <a
                className="text-primary-black transition-colors duration-200 hover:text-primary-magenta dark:hover:text-primary-pink"
                href={articles_feed_path()}
                onClickCapture={handleViewAllClick}
              >
                {VIEW_LATEST_ARTICLES}
              </a>
            </div>
          )}
        </InView>
      </Track>
    </ForYouSection>
  );
};

export default ForYouRecommendedArticles;
